import React, { ComponentProps, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { MultiSelect } from "@bonliva-crm/ui";
import locale from "@bonliva-crm/locale";
import { useUpdateEffect } from "@hooks";
import { FilterWithFavouriteProps } from "../../utils/filters-record-types";
import { ControllableCollapsibleFavFilter } from "../_base/controllable-collapsible-fav-filter";
import { isEmpty } from "lodash";

type Option<T> = T & { label: any; value: any };

export type MultiSelectFilterProps<T> = {
  label: string;
  options: Option<T>[];
  isLoading?: boolean;
} & FilterWithFavouriteProps;

export function MultiSelectFilter<T>({
  label,
  options,
  ...props
}: MultiSelectFilterProps<T>) {
  const selectRef = useRef<any>(null);
  const { setValue, resetField } = useFormContext();
  const { field, fieldState } = useController({ name: props.name });
  const [expanded, setExpanded] = useState(fieldState.isDirty);

  useUpdateEffect(() => {
    if (expanded && selectRef.current) {
      (selectRef.current as any).focus();
    }
  }, [expanded]);

  const clear = () => resetField(props.name, { defaultValue: [] });

  const badgeContent = !isEmpty(field.value)
    ? options.find((o) => o.value === field.value[0])?.label
    : null;

  const commonProps: ComponentProps<typeof MultiSelect<Option<T>>> = {
    options,
    name: props.name,
    value: field?.value?.map((v: string) => options.find((x) => v === x.value)) ?? [],
    onChange: (selected) => {
      setValue(
        props.name,
        selected.map((s) => s.value),
        {
          shouldDirty: true
        }
      );
    },
    hideSelectedOptions: false,
    controlShouldRenderValue: false,
    placeholder: label,
    isSearchable: true,
    maxMenuHeight: 300,
    showBadge: field?.value !== null,
    badgeContent,
    optionType: "checkbox",
    isClearable: field?.value !== null,
    onClear: clear,
    closeMenuOnSelect: false,
    hidePlaceholderOnFocus: false,
    showOptionDivider: false,
    isLoading: props.isLoading
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={label}
        isDirty={fieldState.isDirty}
        isClearable={fieldState.isDirty}
        isExpanded={expanded}
        onClear={clear}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <MultiSelect<Option<T>>
          {...commonProps}
          selectRef={selectRef}
          isClearable={false}
          openMenuOnFocus
          menuPlacement="auto"
          menuPortalTarget={document.body}
          controlShouldRenderValue
          showBadge={false}
          placeholder={locale.SelectValue}
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <MultiSelect<Option<T>>
      {...commonProps}
      styleProps={{
        control: { showBorder: true },
        menu: { showBorder: true }
      }}
    />
  );
}
